import React from 'react';
import { Action, createLocalContextWithActionHandlers } from 'App/helpers/stateHelper';
import Actions from './Actions';
export { type Dispatch } from 'App/helpers/stateHelper';
import { LOCAL_STORAGE_KEY, CACHE_EXPIRY_HOURS } from './Helpers';
import LocalStorage from 'Components/Helpers/LocalStorage';
import { ChatAction } from 'Resources/AIChat';

export type AIChatMessage = {
	id?: string; // Unique message ID, generated when user sends a message
	message: string;
	response?: string;
	loading: boolean;
	action?: ChatAction;
	streaming?: boolean;
	hasError?: boolean;
	reasoning?: string;
	langsmithRunId?: string; // LangSmith run ID for feedback
};

export type ChatSession = {
	id: string;
	name: string;
	createdAt: number;
	updatedAt: number;
	unreadCount?: number;
	nameManuallySet?: boolean;
};

export type AIChatState = {
	messages: AIChatMessage[];
	loading: { [chatId: string]: boolean }; // Loading state per chatId
	message: string;
	feedbackGiven: { [messageIndex: number]: 'up' | 'down' };
	currentChatId: string | null;
	chats: ChatSession[];
	isChatSidebarExpanded: boolean;
};

const ACTION_HANDLERS: { [key: string]: (s: AIChatState, a: Action) => AIChatState } = {
	[Actions.SET_MESSAGES]: (state, { messages, chatId }) => {
		const localStorage = new LocalStorage();
		// Use provided chatId or fall back to currentChatId
		const targetChatId = chatId || state.currentChatId;

		// Only update state.messages if these messages belong to the current chat
		// This prevents switching active chat when messages are stored for a different chat
		const shouldUpdateMessages = !chatId || chatId === state.currentChatId;

		// Load existing messages from localStorage to preserve reasoning
		let existingMessages: AIChatMessage[] = [];
		if (targetChatId) {
			const stored = localStorage.getValue(LOCAL_STORAGE_KEY) as string | null;
			if (stored) {
				try {
					const allChats = JSON.parse(stored) as Record<
						string,
						{ messages: AIChatMessage[]; timestamp: number }
					>;
					existingMessages = allChats[targetChatId]?.messages || [];
				} catch (err) {
					console.error(err);
				}
			}
		}

		// Merge reasoning from existing messages (in localStorage) with new messages
		// Match by message ID for reliable pairing
		const finalMessages = messages.map((msg: AIChatMessage) => {
			// Try to find existing message by ID in localStorage
			const existingMsg = existingMessages.find((m: AIChatMessage) => m.id === msg.id);

			// Also check current state if it's the same chat
			const currentMsg =
				shouldUpdateMessages && chatId === state.currentChatId
					? state.messages.find((m: AIChatMessage) => m.id === msg.id)
					: null;

			// Prefer reasoning from localStorage (most up-to-date), then current state, then new message
			const reasoning = existingMsg?.reasoning || currentMsg?.reasoning || msg.reasoning;

			if (reasoning && reasoning !== msg.reasoning) {
				return { ...msg, reasoning };
			}
			return msg;
		});

		if (targetChatId) {
			const stored = localStorage.getValue(LOCAL_STORAGE_KEY) as string | null;
			let allChats: Record<string, { messages: AIChatMessage[]; timestamp: number }> = {};
			try {
				if (stored) {
					allChats = JSON.parse(stored);
				}
			} catch (err) {
				console.error(err);
			}

			// Store the final messages with preserved reasoning
			allChats[targetChatId] = { messages: finalMessages, timestamp: Date.now() };
			localStorage.setValue(LOCAL_STORAGE_KEY, JSON.stringify(allChats));
		}

		return {
			...state,
			messages: shouldUpdateMessages ? finalMessages : state.messages
		};
	},
	[Actions.SET_LOADING]: (state, { loading, chatId }) => {
		if (!chatId) {
			// If no chatId provided, don't update loading state
			return state;
		}
		return {
			...state,
			loading: {
				...state.loading,
				[chatId]: loading
			}
		};
	},
	[Actions.SET_MESSAGE]: (state, { message }) => ({
		...state,
		message
	}),
	[Actions.CLEAR_MESSAGES]: (state, { chatId }) => {
		if (chatId && chatId !== state.currentChatId) {
			return state;
		}
		const newLoading = { ...state.loading };
		if (state.currentChatId) {
			newLoading[state.currentChatId] = false;
		}
		return {
			...state,
			loading: newLoading,
			messages: [],
			message: ''
		};
	},
	[Actions.SET_MESSAGE_ERROR]: (state, { messageIndex }) => {
		const newMessages = [...state.messages];
		if (newMessages[messageIndex]) {
			newMessages[messageIndex] = {
				...newMessages[messageIndex],
				loading: false,
				hasError: true
			};
		}
		return {
			...state,
			messages: newMessages
		};
	},
	[Actions.SET_FEEDBACK]: (state, { messageIndex, feedbackType }) => {
		// If messageIndex is -1, clear all feedback
		if (messageIndex === -1) {
			return {
				...state,
				feedbackGiven: {}
			};
		}

		return {
			...state,
			feedbackGiven: {
				...state.feedbackGiven,
				[messageIndex]: feedbackType
			}
		};
	},
	[Actions.SET_REASONING_FOR_MESSAGE_ID]: (state, { chatId, messageId, reasoning }) => {
		const localStorage = new LocalStorage();

		// Always store reasoning in localStorage for the correct chat, even if it's not the current chat
		if (chatId && messageId) {
			const stored = localStorage.getValue(LOCAL_STORAGE_KEY) as string | null;
			let allChats: Record<string, { messages: AIChatMessage[]; timestamp: number }> = {};
			try {
				if (stored) {
					allChats = JSON.parse(stored);
				}
			} catch (err) {
				console.error(err);
			}

			// Get messages for this chat
			const chatMessages = allChats[chatId]?.messages || [];
			const targetMessageIndex = chatMessages.findIndex((msg: AIChatMessage) => msg.id === messageId);

			if (targetMessageIndex >= 0) {
				const targetMessage = chatMessages[targetMessageIndex];
				const existingReasoning = targetMessage.reasoning || '';
				// Check if the new reasoning already exists to prevent duplicates
				const existingReasonings = existingReasoning.split('\n').filter(f => f.trim());
				const isDuplicate = existingReasonings.some(f => f.trim() === reasoning.trim());

				if (!isDuplicate) {
					const newReasoning = existingReasoning ? `${existingReasoning}\n${reasoning}` : reasoning;
					chatMessages[targetMessageIndex] = {
						...targetMessage,
						reasoning: newReasoning
					};

					// Save updated messages back to localStorage
					allChats[chatId] = { messages: chatMessages, timestamp: allChats[chatId]?.timestamp || Date.now() };
					localStorage.setValue(LOCAL_STORAGE_KEY, JSON.stringify(allChats));
				}
			} else {
				// Message not found in localStorage - this can happen if reasoning arrives before message is saved
				// Try to find it by looking for the last loading/streaming message
				const loadingMessageIndex = chatMessages.findIndex(
					(msg: AIChatMessage) => msg.loading || msg.streaming
				);
				if (loadingMessageIndex >= 0) {
					const targetMessage = chatMessages[loadingMessageIndex];
					const existingReasoning = targetMessage.reasoning || '';
					const existingReasonings = existingReasoning.split('\n').filter(f => f.trim());
					const isDuplicate = existingReasonings.some(f => f.trim() === reasoning.trim());

					if (!isDuplicate) {
						const newReasoning = existingReasoning ? `${existingReasoning}\n${reasoning}` : reasoning;
						chatMessages[loadingMessageIndex] = {
							...targetMessage,
							reasoning: newReasoning
						};

						allChats[chatId] = {
							messages: chatMessages,
							timestamp: allChats[chatId]?.timestamp || Date.now()
						};
						localStorage.setValue(LOCAL_STORAGE_KEY, JSON.stringify(allChats));
					}
				} else if (chatMessages.length > 0) {
					// Fallback: add reasoning to the last message
					const lastMessage = chatMessages[chatMessages.length - 1];
					const existingReasoning = lastMessage.reasoning || '';
					const existingReasonings = existingReasoning.split('\n').filter(f => f.trim());
					const isDuplicate = existingReasonings.some(f => f.trim() === reasoning.trim());

					if (!isDuplicate) {
						const newReasoning = existingReasoning ? `${existingReasoning}\n${reasoning}` : reasoning;
						chatMessages[chatMessages.length - 1] = {
							...lastMessage,
							reasoning: newReasoning
						};

						allChats[chatId] = {
							messages: chatMessages,
							timestamp: allChats[chatId]?.timestamp || Date.now()
						};
						localStorage.setValue(LOCAL_STORAGE_KEY, JSON.stringify(allChats));
					}
				}
			}
		}

		// Only update state.messages if this reasoning belongs to the current chat
		if (chatId && chatId !== state.currentChatId) {
			return state;
		}

		const newMessages = [...state.messages];
		const targetMessageIndex = newMessages.findIndex(msg => msg.id === messageId);

		if (targetMessageIndex >= 0) {
			const targetMessage = newMessages[targetMessageIndex];
			const existingReasoning = targetMessage.reasoning || '';
			// Check if the new reasoning already exists to prevent duplicates
			const existingReasonings = existingReasoning.split('\n').filter(f => f.trim());
			const isDuplicate = existingReasonings.some(f => f.trim() === reasoning.trim());

			if (!isDuplicate) {
				const newReasoning = existingReasoning ? `${existingReasoning}\n${reasoning}` : reasoning;
				newMessages[targetMessageIndex] = {
					...targetMessage,
					reasoning: newReasoning
				};
			}
		}

		return {
			...state,
			messages: newMessages
		};
	},
	[Actions.INCREMENT_UNREAD_COUNT]: (state, { chatId }) => {
		if (chatId === state.currentChatId) {
			return state;
		}
		const newState = {
			...state,
			chats: state.chats.map(c => (c.id === chatId ? { ...c, unreadCount: (c.unreadCount || 0) + 1 } : c))
		};

		// Broadcast unread count update
		if (typeof Tools !== 'undefined' && Tools.$rootScope) {
			const totalUnread = newState.chats.reduce(
				(sum, c) => sum + (c.id === state.currentChatId ? 0 : c.unreadCount || 0),
				0
			);
			Tools.$rootScope.$broadcast('AIChat.unreadCount', totalUnread);
		}

		return newState;
	},
	[Actions.MARK_CHAT_AS_READ]: (state, { chatId }) => {
		const newState = {
			...state,
			chats: state.chats.map(c => (c.id === chatId ? { ...c, unreadCount: 0 } : c))
		};

		// Broadcast unread count update
		if (typeof Tools !== 'undefined' && Tools.$rootScope) {
			const totalUnread = newState.chats.reduce((sum, c) => sum + (c.id === chatId ? 0 : c.unreadCount || 0), 0);
			Tools.$rootScope.$broadcast('AIChat.unreadCount', totalUnread);
		}

		return newState;
	},
	[Actions.SET_CURRENT_CHAT_ID]: (state, { chatId }) => ({
		...state,
		currentChatId: chatId
	}),
	[Actions.SET_CHATS]: (state, { chats }) => ({
		...state,
		chats
	}),
	[Actions.ADD_CHAT]: (state, { chat }) => ({
		...state,
		chats: [chat, ...state.chats]
	}),
	[Actions.UPDATE_CHAT]: (state, { chatId, updates }) => ({
		...state,
		chats: state.chats.map(c => (c.id === chatId ? { ...c, ...updates } : c))
	}),
	[Actions.DELETE_CHAT]: (state, { chatId }) => ({
		...state,
		chats: state.chats.filter(c => c.id !== chatId),
		currentChatId: state.currentChatId === chatId ? state.chats[0]?.id || null : state.currentChatId,
		messages: state.currentChatId === chatId ? [] : state.messages
	}),
	[Actions.SET_SIDEBAR_EXPANDED]: (state, { expanded }) => ({
		...state,
		isChatSidebarExpanded: expanded
	})
};

const { Provider, useDispatch, useSelector } = createLocalContextWithActionHandlers(ACTION_HANDLERS);
export const useAIChatDispatch = useDispatch;
export const useAIChatSelector = useSelector;

export function AIChatProvider({ children }: { children: React.ReactNode }) {
	const localStorage = new LocalStorage();

	const { storedChats } = (() => {
		try {
			const stored = localStorage.getValue(LOCAL_STORAGE_KEY) as string | null;
			if (stored) {
				// Try to parse as new format (object with chatId keys)
				try {
					const parsed = JSON.parse(stored) as Record<
						string,
						{ messages: AIChatMessage[]; timestamp: number }
					>;
					if (typeof parsed === 'object' && !Array.isArray(parsed)) {
						// New format
						const now = Date.now();
						const validChats: Record<string, { messages: AIChatMessage[]; timestamp: number }> = {};

						for (const [chatId, data] of Object.entries(parsed)) {
							const expiryTime = data.timestamp + CACHE_EXPIRY_HOURS * 60 * 60 * 1000;
							if (now < expiryTime) {
								validChats[chatId] = data;
							}
						}

						if (Object.keys(validChats).length === 0) {
							localStorage.setValue(LOCAL_STORAGE_KEY, '{}');
						} else {
							localStorage.setValue(LOCAL_STORAGE_KEY, JSON.stringify(validChats));
						}

						return { storedChats: [] };
					}
				} catch {
					// Old format, migrate
					const parsed = JSON.parse(stored) as {
						messages: AIChatMessage[];
						timestamp: number;
					};
					const now = Date.now();
					const expiryTime = parsed.timestamp + CACHE_EXPIRY_HOURS * 60 * 60 * 1000;

					if (now < expiryTime && parsed.messages.length > 0) {
						const chatId = Date.now().toString();
						const migrated = { [chatId]: { messages: parsed.messages, timestamp: parsed.timestamp } };
						localStorage.setValue(LOCAL_STORAGE_KEY, JSON.stringify(migrated));
						return { storedChats: [] };
					}
				}
			}
		} catch (err) {
			console.error(err);
			localStorage.setValue(LOCAL_STORAGE_KEY, '{}');
		}
		return { storedChats: [] };
	})();

	const initialState = {
		messages: [],
		loading: {},
		message: '',
		feedbackGiven: {},
		currentChatId: null,
		chats: storedChats,
		isChatSidebarExpanded: true
	};

	return <Provider value={initialState}>{children}</Provider>;
}
